import { useTranslation } from "react-i18next";

import { FormHelperText, InputAdornment, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { formatEuro } from "../../../../../../../../../../format/formatNumber";
import { FormikDecimalTextField } from "../../../../../../../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../../../../../formik/FormikYesNoAutocomplete";
import {
  AssetIncomeFormValues,
  INCOME_MONTH_COUNT,
  IncomeEntryFormValue,
  IncomeMaxLength,
} from "../../../../../../IncomeView.types";
import { calculateAverage, formatMonthKey } from "../../../../../../IncomeView.utils";

const SECTION = "clients.client.debtSettlement.assetInventory.incomeTab.average";

interface Props {
  formik: FormikProps<AssetIncomeFormValues>;
  index: number;
  entry: IncomeEntryFormValue;
  isNonSeizable: boolean;
}

export function IncomeAverageFields({ formik, index, entry, isNonSeizable }: Props) {
  const { t, i18n } = useTranslation();
  const average = calculateAverage(entry, isNonSeizable);
  const usedMonthNames = average.usedMonths.map((month) => formatMonthKey(month.month));

  const basis =
    average.usedMonths.length === INCOME_MONTH_COUNT
      ? t(`${SECTION}.basisAll`)
      : average.usedMonths.length > 1
        ? t(`${SECTION}.basisSome`, { months: usedMonthNames.join(` ${t(`${SECTION}.and`)} `) })
        : average.usedMonths.length === 1
          ? t(`${SECTION}.basisOne`, { month: usedMonthNames[0] })
          : "";

  const label = `${t(isNonSeizable ? `${SECTION}.averageIncome` : `${SECTION}.relevantAverageIncome`)} ${basis}`.trim();
  const path = (field: keyof IncomeEntryFormValue) => `incomes.${index}.${field}` as const;
  const averageId = `income-${entry.key}-average`;

  return (
    <Stack gap={1}>
      <Stack gap={0.5} aria-live="polite" sx={{ mb: 3, mt: 0.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
          <Typography id={averageId} variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }} aria-labelledby={averageId}>
            {average.amount === null ? "–" : formatEuro(average.amount, i18n.language)}
          </Typography>
        </Stack>
        {average.amount === null && formik.submitCount > 0 && (
          <FormHelperText error sx={{ m: 0 }}>
            {t("clients.client.debtSettlement.assetInventory.incomeTab.validation.checkIncome")}
          </FormHelperText>
        )}
      </Stack>
      <FormikYesNoAutocomplete
        id={`income-${entry.key}-representative`}
        formik={formik}
        path={path("isAverageRepresentative")}
        label={t(`${SECTION}.isRepresentative`)}
      />
      {entry.isAverageRepresentative === false && (
        <>
          <FormikTextField
            formik={formik}
            path={path("nonRepresentativeReason")}
            label={t(`${SECTION}.nonRepresentativeReason`)}
            multiline
            minRows={3}
            slotProps={{ input: { inputProps: { maxLength: IncomeMaxLength.nonRepresentativeReason } } }}
          />
          <FormikDecimalTextField
            id={`income-${entry.key}-actual-average`}
            formik={formik}
            path={path("actualAverageIncome")}
            label={t(`${SECTION}.actualAverageIncome`)}
            decimalScale={2}
            minValue={0}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
          />
        </>
      )}
    </Stack>
  );
}
